import snakeCaseKeys from 'snakecase-keys';

import { restClient, State } from '../..';

export enum CampusPlan {
  lite = 'lite',
  flex = 'flex',
  complete = 'complete'
}

export type PlanLite = {
  type: CampusPlan.lite;
};

export type PlanFlex = {
  type: CampusPlan.flex;
  therapy: number;
  psychiatry: number;
};

export enum UnlimitedType {
  both = 'both',
  therapy = 'therapy',
  psychiatry = 'psychiatry',
}

export type PlanComplete = {
  type: CampusPlan.complete;
  unlimitedType: UnlimitedType;
};

export type ContractInput = {
  plan: PlanLite | PlanFlex | PlanComplete;
  period: {
    fromDate: string;
    toDate: string;
  };
};

export type ContractData = ContractInput & {
  id: string;
  createdAt: string;
  createdBy: string;
  updatedAt: string;
  updatedBy?: string;
};

export const enum HigherEdOrgStatus {
  active = 'active',
  inactive = 'inactive',
}

export type HigherEdOrgCreatePayload = {
  org: {
    id?: string;
    name: string;
    stateId: string;
    contract: ContractInput;
  };
  invite: {
    email: string;
    firstName: string;
    lastName: string;
  };
};

export type HigherEdOrgCreateResponse = {
  org: HigherEdOrg;
};

export type HigherEdOrgUpdatePayload = {
  name?: string;
  stateId?: string;
  state?: State;
  contract?: ContractInput;
};

export type HigherEdOrg = {
  id?: string;
  createdAt?: string;
  updatedAt?: string;
  status?: HigherEdOrgStatus;
  name: string;
  state: State;
  contract: ContractData;
};

export type HigherEdOrgView = Omit<HigherEdOrg, 'status' | 'contract'>;

export const getHigherEdOrg = async (id: string) => {
  return restClient.request<{ data: HigherEdOrg }>(`/api/v1/higher_ed/org/${id}`);
};

export const getHigherEdOrgView = async (id: string) => {
  return restClient.request<{ data: HigherEdOrgView }>(`/api/v1/view/higher_ed/org/${id}`);
};

export const getHigherEdOrgs = async () => {
  return restClient.request<{ data: HigherEdOrg[] }>('/api/v1/higher_ed/org');
};

export const createHigherEdOrg = async (data: HigherEdOrgCreatePayload) => {
  return restClient.request<{ data: HigherEdOrgCreateResponse }>('/api/v1/higher_ed/org',
    { method: 'POST', data: snakeCaseKeys(data) });
};

export const updateHigherEdOrg = async (orgId: string, data: HigherEdOrgUpdatePayload ) => {
  return restClient.request<{ data: HigherEdOrg }>(`/api/v1/higher_ed/org/${orgId}`,
    { method: 'PUT', data: snakeCaseKeys(data) });
};

export const updateHigherEdOrgContract = async (orgId: string, contract: ContractData) => {
  const { plan, period } = snakeCaseKeys(contract);

  return restClient.request<{ data: HigherEdOrg }>(`/api/v1/higher_ed/org/${orgId}/contract/${contract.id}`,
    { method: 'PUT', data: { plan, period } });
};
